import React from 'react'
import { Box, Typography, List, ListItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import LoadingSpinner from 'components/loading-spinner'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  chartContainer: {
    height: 220,
    width: '100%',
    margin: '0',
    marginBottom: theme.spacing(2),
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  titleMargin: {
    marginBottom: theme.spacing(3),
  },
  legendContainer: {
    width: '100%',
  },
  legendList: {
    padding: 0,
    width: '100%',
  },
  legendItem: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
  },
  colorBox: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  legendText: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  legendLabel: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#333',
  },
  legendValue: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#00205B',
  },
}))

// Custom colors for pie chart slices - PGA brand colors
const COLORS = [
  '#00205B', // PGA Navy Blue
  '#2053FF', // PGA Sport Blue
  '#89C1D3', // Birdie Blue
  '#F1F1F1', // Trophy Silver (whiteish)
  '#97999B', // Steel Gray
]

// Simulated data based on screenshot
const DEMO_TOP_BOOKED_DATA = [
  {
    lessonType: { title: 'Private Instruction', id: '1' },
    lessonCount: 186,
    uniqueStudentCount: 73,
    earnings: 18600,
  },
  {
    lessonType: { title: 'Private Instruction - FNF', id: '2' },
    lessonCount: 24,
    uniqueStudentCount: 15,
    earnings: 2400,
  },
  {
    lessonType: { title: '2 hours Private Instruction', id: '3' },
    lessonCount: 18,
    uniqueStudentCount: 10,
    earnings: 2700,
  },
  {
    lessonType: { title: 'Private Instruction - Media', id: '4' },
    lessonCount: 8,
    uniqueStudentCount: 6,
    earnings: 800,
  },
  {
    lessonType: { title: 'Holiday Lesson Pack', id: '5' },
    lessonCount: 5,
    uniqueStudentCount: 3,
    earnings: 350,
  },
]

// Custom tooltip component
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        style={{
          backgroundColor: '#fff',
          padding: '10px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography
          variant="body2"
          style={{ fontWeight: 600, color: '#00205B' }}
        >
          {payload[0].name}
        </Typography>
        <Typography variant="body2">
          {`Bookings: ${payload[0].value}`} ({payload[0].payload.percentage}%)
        </Typography>
        <Typography
          variant="body2"
          style={{ color: '#00205B', fontWeight: 500 }}
        >
          Revenue: ${payload[0].payload.earnings.toLocaleString()}
        </Typography>
        <Typography variant="body2">
          Unique Students: {payload[0].payload.uniqueStudentCount}
        </Typography>
      </Box>
    )
  }
  return null
}

const TopBookedLessonsChart = ({ data, loading, error }) => {
  const classes = useStyles()

  const renderContent = () => {
    if (loading) return <LoadingSpinner />
    if (error) {
      return <Box className={classes.noData}>Error loading chart data</Box>
    }

    // Use simulated data if no data is returned from the API
    const dataToUse = !data || data.length === 0 ? DEMO_TOP_BOOKED_DATA : data

    // Calculate the total number of bookings to determine percentages
    const totalBookings = dataToUse.reduce(
      (total, item) => total + item.lessonCount,
      0,
    )

    // Prepare data for pie chart
    const chartData = dataToUse.map((item, index) => ({
      name: item.lessonType.title,
      value: item.lessonCount,
      percentage: Math.round((item.lessonCount / totalBookings) * 100),
      earnings: item.earnings,
      uniqueStudentCount: item.uniqueStudentCount,
      color: COLORS[index % COLORS.length],
    }))

    return (
      <>
        {/* Chart container with fixed height */}
        <Box className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                innerRadius={0}
                paddingAngle={0}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percentage }) => `${percentage}%`}
                labelStyle={{
                  fill: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend */}
        <Box className={classes.legendContainer}>
          <List className={classes.legendList} disablePadding>
            {chartData.map((entry, index) => (
              <ListItem
                key={`legend-${index}`}
                className={classes.legendItem}
                disableGutters
              >
                <Box
                  className={classes.colorBox}
                  style={{ backgroundColor: entry.color }}
                />
                <Box className={classes.legendText}>
                  <Typography className={classes.legendLabel}>
                    {entry.name}
                  </Typography>
                  <Typography className={classes.legendValue}>
                    ({entry.percentage}%)
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </>
    )
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.titleMargin}>
        Top Booked Lesson Types
      </Typography>
      {renderContent()}
    </Box>
  )
}

export default TopBookedLessonsChart
